import React, { useMemo } from 'react';
import { Outlet } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { faCalendarAlt, faUsers, faCog, faBuilding } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserSecret } from '@fortawesome/free-solid-svg-icons';
import { RootState, AppDispatch } from '../../store';
import { switchAccount } from '../../store/accountsSlice';
import Logo from '../common/Logo';
import { SidebarMenu, SidebarMenuItem } from '../common/SidebarMenu';
import { UserMenu } from '../common/UserMenu';

export default function DashboardLayout() {
    const dispatch = useDispatch<AppDispatch>();
    const userRole = useSelector((state: RootState) => state.auth.user?.role);
    const isAdmin = userRole === 'admin';
    const canManageUsers = userRole === 'admin' || userRole === 'gamemaster';
    const { impersonatedAccountId, impersonatedAccountName, isSwitching } = useSelector(
        (state: RootState) => state.accounts
    );

    const menuItems: SidebarMenuItem[] = useMemo(() => {
        const items: SidebarMenuItem[] = [
            { to: '/dashboard', icon: faCalendarAlt, label: 'Events', end: true },
        ];
        if (isAdmin) {
            items.push({ to: '/dashboard/accounts', icon: faBuilding, label: 'Accounts' });
        }
        if (canManageUsers) {
            items.push({ to: '/dashboard/users', icon: faUsers, label: 'Users' });
        }
        items.push({ to: '/dashboard/settings', icon: faCog, label: 'Settings' });
        return items;
    }, [isAdmin, canManageUsers]);

    return (
        <div className="d-flex h-100">
            <div className="col-2 h-100 p-0 d-flex flex-column bg-brand-surface border-end">
                <div className="p-3 text-center border-bottom">
                    <Logo variant="horizontal" maxHeight={128} />
                </div>

                <SidebarMenu items={menuItems} />

                {/* User menu at the bottom of sidebar */}
                <div className="mt-auto p-2 border-top">
                    <UserMenu />
                </div>
            </div>
            <div className="col-10 h-100 p-0 bg-brand-dark d-flex flex-column">
                {impersonatedAccountId && (
                    <Alert
                        variant="warning"
                        className="d-flex justify-content-between align-items-center mb-0 rounded-0"
                    >
                        <span>
                            <FontAwesomeIcon icon={faUserSecret} className="me-2" />
                            Operating as{' '}
                            <strong>{impersonatedAccountName || 'another account'}</strong>. All
                            actions apply to this account.
                        </span>
                        <Button
                            variant="outline-dark"
                            size="sm"
                            onClick={() => dispatch(switchAccount(null))}
                            disabled={isSwitching}
                        >
                            {isSwitching ? (
                                <>
                                    <Spinner
                                        as="span"
                                        animation="border"
                                        size="sm"
                                        role="status"
                                        aria-hidden="true"
                                        className="me-2"
                                    />
                                    Exiting...
                                </>
                            ) : (
                                'Exit impersonation'
                            )}
                        </Button>
                    </Alert>
                )}
                <div className="flex-grow-1 overflow-auto">
                    <Outlet />
                </div>
            </div>
        </div>
    );
}
